import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import {
  Fuel, Wallet, BarChart3, Camera, Upload, ClipboardCheck, Flag,
  Sparkles, ChevronRight, Users, ShoppingCart, Package, Clock
} from "lucide-react";

/**
 * RoleHome — role-aware landing screen.
 * Instead of a wall of KPI numbers, show 2-4 large "your job here" cards.
 * Owners/Admins still see the full analytics Dashboard via /analytics.
 */
export default function RoleHome() {
  const { user } = useAuth();
  const role = user?.role || "filler";
  const cards = TASKS[role] || TASKS.cashier;
  const roleLabel = ROLE_LABEL[role] || role;

  return (
    <div className="max-w-4xl mx-auto space-y-5 pb-16" data-testid="role-home-root">
      {/* Hero */}
      <div className="rounded-sm border-2 border-slate-900 bg-white p-4 sm:p-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Signed in as</span>
          <span className={"px-2 py-0.5 text-xs font-bold border-2 rounded-sm " + ROLE_BADGE[role]}>{roleLabel}</span>
        </div>
        <h1 className="text-2xl sm:text-4xl font-black tracking-tight text-slate-900 mt-2">
          Hi {user?.name?.split(" ")[0] || "there"} — here&apos;s what you do here.
        </h1>
        <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-2xl">
          {ROLE_INTRO[role] || "These are the things you own on this app. Tap a card to jump straight in."}
        </p>
        <Link to="/tour" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-700 hover:text-slate-900 mt-3">
          <Sparkles size={12} /> Full 3-minute tour of the app →
        </Link>
      </div>

      {/* Task cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="block" data-testid={`role-home-card-${c.to.replace(/[^a-z]/gi, "")}`}>
            <Card className={"border-2 rounded-sm p-4 sm:p-5 hover:shadow-md transition-shadow " + c.color}>
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-sm p-2 border-2 border-current opacity-90 shrink-0">
                  <c.icon size={22} strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900">{c.title}</h3>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white border border-current rounded-sm uppercase tracking-wider">{c.freq}</span>
                  </div>
                  <p className="text-sm text-slate-700 mt-1 leading-relaxed">{c.text}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-800">
                    Open <ChevronRight size={12} />
                  </div>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      {/* Owner/Admin extras — link to the analytics dashboard */}
      {(role === "owner" || role === "admin") && (
        <div className="text-center pt-2">
          <Link to="/analytics" className="inline-flex items-center gap-1 text-sm font-bold text-slate-700 hover:text-slate-900 underline">
            View full analytics dashboard →
          </Link>
        </div>
      )}
    </div>
  );
}

const ROLE_LABEL = {
  filler: "Filler",
  cashier: "Cashier",
  assistant_manager: "Supervisor",
  manager: "Manager",
  admin: "Admin",
  owner: "Owner",
};

const ROLE_BADGE = {
  filler: "border-emerald-500 text-emerald-800 bg-emerald-50",
  cashier: "border-blue-500 text-blue-800 bg-blue-50",
  assistant_manager: "border-purple-500 text-purple-800 bg-purple-50",
  manager: "border-indigo-500 text-indigo-800 bg-indigo-50",
  admin: "border-slate-500 text-slate-800 bg-slate-50",
  owner: "border-amber-500 text-amber-800 bg-amber-50",
};

const ROLE_INTRO = {
  filler: "Your job is simple: record meter readings and click photos. That's it. Tap the green card below to start your shift.",
  cashier: "You close the shift. Take the DRAFT the filler handed you, add payments and cash, upload proofs, hit submit.",
  assistant_manager: "You watch the whole floor. Review DRAFT shifts, close reconciliation, acknowledge flags, and drill into any number the owner asks about.",
  manager: "You're the daily anchor. Manager Daily, order fuel, watch red flags, resolve red flags, run the team.",
  admin: "You configure the system. Users, masters, integrations, and rescue any locked entry.",
  owner: "Everything. Dashboards, drill-through, flags, escalations, config.",
};

const CARD_COLORS = {
  green: "border-emerald-400 bg-emerald-50/60",
  blue: "border-blue-400 bg-blue-50/60",
  purple: "border-purple-400 bg-purple-50/60",
  indigo: "border-indigo-400 bg-indigo-50/60",
  amber: "border-amber-400 bg-amber-50/60",
  teal: "border-teal-400 bg-teal-50/60",
  slate: "border-slate-400 bg-slate-50/60",
  orange: "border-orange-400 bg-orange-50/60",
  red: "border-red-400 bg-red-50/60",
};

const TASKS = {
  filler: [
    { to: "/filler", title: "Punch your shift", icon: Fuel, freq: "1× per shift", color: CARD_COLORS.green,
      text: "Pick your nozzles, scan closing readings with the camera, upload photos, hit submit. 90 seconds." },
    { to: "/uploads", title: "Add missed photos", icon: Camera, freq: "as needed", color: CARD_COLORS.blue,
      text: "If you forgot a meter photo or a slip, drop it here — the system will bind it to the right slot." },
    { to: "/shifts", title: "See my past shifts", icon: ClipboardCheck, freq: "reference", color: CARD_COLORS.slate,
      text: "Your recent shift entries in one place — the cashier will pick them up from DRAFT and close them." },
    { to: "/flags", title: "My flags", icon: Flag, freq: "when notified", color: CARD_COLORS.red,
      text: "If a photo you took was unreadable or a reading looked off, you'll see it here — re-upload or explain." },
  ],
  cashier: [
    { to: "/shifts", title: "Close today's shift", icon: Wallet, freq: "1× per shift", color: CARD_COLORS.blue,
      text: "Open the DRAFT the filler handed you. Add payments (all active modes pre-listed), debtor sales, cash-in-hand. Submit." },
    { to: "/uploads", title: "Attach payment screenshots", icon: Upload, freq: "1× per shift", color: CARD_COLORS.purple,
      text: "PhonePe, Paytm, Card — grab daily-total screenshots and drop them here. Even ₹0 modes need a proof." },
    { to: "/reconciliation", title: "Reconcile cash slip", icon: BarChart3, freq: "1× per shift", color: CARD_COLORS.amber,
      text: "Type the CMS slip amount → variance flips green if it matches expected cash. Any red variance auto-flags." },
    { to: "/debtors", title: "Update debtor payments", icon: Users, freq: "on receipt", color: CARD_COLORS.teal,
      text: "When Afcons/Bhoir pays their bill, record it here." },
  ],
  assistant_manager: [
    { to: "/review-queue", title: "Review DRAFT shifts", icon: ClipboardCheck, freq: "several × per day", color: CARD_COLORS.purple,
      text: "Every DRAFT shift lands here. Confirm readings, approve payments, or bounce back with a comment." },
    { to: "/reconciliation", title: "Close reconciliation", icon: BarChart3, freq: "1× per shift", color: CARD_COLORS.amber,
      text: "Confirm the CMS slip amount matches expected cash. Close the recon for the shift." },
    { to: "/flags", title: "Acknowledge flags", icon: Flag, freq: "morning + evening", color: CARD_COLORS.red,
      text: "Every open flag with an actor — investigate, ack, or escalate to Manager." },
    { to: "/fraud", title: "Watch the floor", icon: Sparkles, freq: "daily", color: CARD_COLORS.indigo,
      text: "Repeat-offender table, cashier × shift matrix, per-nozzle adjustment heat list." },
  ],
  manager: [
    { to: "/manager-daily", title: "Manager Daily entry", icon: ClipboardCheck, freq: "1× per day", color: CARD_COLORS.indigo,
      text: "Sales, shortages, trials, filler count per shift, air station status. Prefilled — you just confirm." },
    { to: "/fuel-purchase", title: "Fuel purchase & PO", icon: ShoppingCart, freq: "as needed", color: CARD_COLORS.orange,
      text: "Place PO with IOCL, log deliveries, upload invoices. System auto-links PO ↔ Invoice." },
    { to: "/flags", title: "Resolve HIGH flags", icon: Flag, freq: "same day", color: CARD_COLORS.red,
      text: "You're the only role that can resolve HIGH-severity flags. Owner sees the count." },
    { to: "/attendance", title: "Upload monthly attendance", icon: Clock, freq: "1× per month", color: CARD_COLORS.teal,
      text: "Export from your biometric machine (Excel/CSV), upload here. Hours are computed automatically." },
    { to: "/lubricants", title: "Lubricant purchases", icon: Package, freq: "weekly", color: CARD_COLORS.teal,
      text: "Enter what you bought → auto-generates printable barcodes to stick on stock." },
    { to: "/fraud", title: "Red flags", icon: Sparkles, freq: "weekly", color: CARD_COLORS.purple,
      text: "Repeat-offender cashiers, per-nozzle adjustment gaming, debtor variance, sales drops." },
  ],
  admin: [
    { to: "/users", title: "Users & roles", icon: Users, freq: "on onboarding", color: CARD_COLORS.slate,
      text: "Promote / demote / deactivate. Match each user to their role." },
    { to: "/masters", title: "Master data", icon: Package, freq: "rare", color: CARD_COLORS.indigo,
      text: "Nozzles, payment modes, debtors, fuel prices, lubricants — anything the pump lives on." },
    { to: "/integrations", title: "Integrations", icon: Sparkles, freq: "1× setup", color: CARD_COLORS.amber,
      text: "Google Sheets, Resend email digest, Document AI OCR. Paste keys once." },
    { to: "/analytics", title: "Full analytics dashboard", icon: BarChart3, freq: "anytime", color: CARD_COLORS.blue,
      text: "The classic KPI grid — hsd/ms/cng litres, ₹ totals, drill-through into every number." },
  ],
  owner: [
    { to: "/analytics", title: "Full analytics dashboard", icon: BarChart3, freq: "daily", color: CARD_COLORS.amber,
      text: "Every headline number, drill-through into the underlying shifts. The 30-second morning check-in." },
    { to: "/fraud", title: "Red flags", icon: Sparkles, freq: "weekly", color: CARD_COLORS.red,
      text: "Repeat-offender cashiers, top rupee-impact flags, deposit-late patterns." },
    { to: "/tour", title: "Show the app to a client", icon: Sparkles, freq: "on demand", color: CARD_COLORS.indigo,
      text: "The 8-step storyboard walkthrough — hand this to prospects, auditors, or new managers." },
    { to: "/users", title: "Team & invites", icon: Users, freq: "as needed", color: CARD_COLORS.slate,
      text: "Add / remove people, pre-approve emails, set roles." },
  ],
};
